import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';

import { NotificationService } from '@shared/services/notification.service';
import { PageHeaderComponent } from '@shared/components/layout';
import { DivineServiceTypeManagementService, DivineServiceTypeDto, CreateDivineServiceTypeDto, UpdateDivineServiceTypeDto } from '../../services/divine-service-type-management.service';
import { getApiErrorMessage } from '@core/http/api-error-message.util';

/**
 * Componente para crear y editar tipos de servicios divinos
 */
@Component({
  selector: 'app-divine-service-type-editor',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    PageHeaderComponent
  ],
  templateUrl: './divine-service-type-editor.component.html',
  styleUrl: './divine-service-type-editor.component.scss'
})
export class DivineServiceTypeEditorComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  isLoading = false;
  isSaving = false;
  isEditMode = false;
  typeId: number | null = null;
  currentType: DivineServiceTypeDto | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private service: DivineServiceTypeManagementService,
    private router: Router,
    private route: ActivatedRoute,
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params['id'] && params['id'] !== 'new') {
          this.typeId = +params['id'];
          this.isEditMode = true;
          this.loadType();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Inicializa el formulario
   */
  private initializeForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
      displayName: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(500)]],
      offeringRequired: [false, [Validators.required]],
    });
  }

  /**
   * Carga el tipo de servicio para editar
   */
  private loadType(): void {
    if (!this.typeId) return;

    this.isLoading = true;
    this.cdr.markForCheck();

    this.service.getById(this.typeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (type: DivineServiceTypeDto) => {
          this.currentType = type;
          this.form.patchValue({
            name: type.name,
            displayName: type.displayName,
            offeringRequired: type.offeringRequired,
            sortOrder: 0
          });
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error loading divine service type:', error);
          this.notificationService.error(getApiErrorMessage(error, 'No se pudo cargar el tipo de servicio seleccionado'));
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Guarda el tipo de servicio (crear o actualizar)
   */
  saveType(): void {
    if (!this.form.valid) {
      this.notificationService.warning('Por favor, completa todos los campos correctamente');
      return;
    }

    this.isSaving = true;
    this.cdr.markForCheck();

    const formValue = this.form.value;

    if (this.isEditMode && this.typeId) {
      this.updateType(this.typeId, formValue);
    } else {
      this.createType(formValue);
    }
  }

  /**
   * Crea un nuevo tipo de servicio divino
   */
  private createType(dto: CreateDivineServiceTypeDto): void {
    this.service.create(dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success('Tipo de servicio creado exitosamente');
          setTimeout(() => {
            this.router.navigate(['/configuration/divine-service-types']);
          }, 1000);
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error creating divine service type:', error);
          this.notificationService.error(getApiErrorMessage(
            error,
            'No se pudo crear el tipo de servicio. Revisá los datos cargados e intentá nuevamente'
          ));
          this.isSaving = false;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Actualiza un tipo de servicio divino existente
   */
  private updateType(id: number, dto: UpdateDivineServiceTypeDto): void {
    this.service.update(id, dto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.notificationService.success('Tipo de servicio actualizado exitosamente');
          setTimeout(() => {
            this.router.navigate(['/configuration/divine-service-types']);
          }, 1000);
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.error('Error updating divine service type:', error);
          this.notificationService.error(getApiErrorMessage(
            error,
            'No se pudo actualizar el tipo de servicio. Revisá los datos cargados e intentá nuevamente'
          ));
          this.isSaving = false;
          this.cdr.markForCheck();
        }
      });
  }

  /**
   * Cancela la edición y vuelve a la lista
   */
  cancel(): void {
    this.router.navigate(['/configuration/divine-service-types']);
  }

  /**
   * Obtiene el control del formulario para validación
   */
  getControl(name: string) {
    return this.form.get(name);
  }

  /**
   * Obtiene el título de la página según el modo
   */
  get pageTitle(): string {
    return this.isEditMode ? 'Editar Tipo de Servicio' : 'Nuevo Tipo de Servicio';
  }
}
